import { Link } from "react-router-dom";
import { ArrowRight, Phone, ShieldCheck, Sparkles, Award, Trophy, MapPin, Building, Server, Factory, Warehouse, Hospital, UtensilsCrossed, Plane, SquareParking } from "lucide-react";
import { Button } from "../components/ui/button";
import { Reveal } from "../components/Reveal";
import { company, featuredServices, stats, philosophy, steps, industries, notableProjects } from "../mock";

// Map industry icon keys to lucide-react components
const INDUSTRY_ICONS = {
  building: Building,
  server: Server,
  factory: Factory,
  warehouse: Warehouse,
  hospital: Hospital,
  utensils: UtensilsCrossed,
  plane: Plane,
  parking: SquareParking,
};

function Hero() {
  return (
    <section className="relative min-h-[100vh] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="/images/industrial.jpg"
          alt="High-performance resinous facility floor"
          className="w-full h-full object-cover kenburns"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/85 to-zinc-950/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-zinc-950/40" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 w-full pt-24">
        <div className="max-w-2xl">
          <div className="reveal in-view inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-orange-500/40 bg-orange-500/10 text-orange-300 text-xs font-medium tracking-wide mb-6">
            <Sparkles className="w-3.5 h-3.5" /> 30+ YEARS OF HEAVY COMMERCIAL & INDUSTRIAL EXPERIENCE
          </div>
          <h1 className="font-display font-black text-white text-5xl md:text-7xl leading-[1.02] tracking-tight">
            Industrial Floor Coatings for <span className="text-gradient">Large Facilities</span>
          </h1>
          <p className="mt-6 text-lg text-zinc-300 leading-relaxed max-w-xl">
            High-performance epoxy, resinous and polished-concrete systems engineered for stadiums, data centers,
            manufacturing plants and distribution centers — installed with minimal downtime across Western New York.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <Link to="/contact">
              <Button className="h-12 px-7 bg-orange-500 hover:bg-orange-600 text-white font-semibold text-base">
                Request a Consultation <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <Link to="/gallery">
              <Button variant="outline" className="h-12 px-7 border-zinc-600 bg-white/5 text-white hover:bg-white/10 font-semibold text-base">
                View Our Work
              </Button>
            </Link>
          </div>
          <a href={`tel:${company.phone}`} className="mt-8 inline-flex items-center gap-2 text-zinc-300 hover:text-orange-400 transition-colors">
            <Phone className="w-4 h-4 text-orange-500" /> <span className="font-medium">{company.phone}</span>
            <span className="text-zinc-500">· {company.hours}</span>
          </a>
        </div>
      </div>
    </section>
  );
}

function IndustriesSection() {
  return (
    <section className="py-24 bg-zinc-950 border-t border-zinc-900">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <SectionHeading
            eyebrow="Industries We Serve"
            title="Trusted by the Region's Most Demanding Facilities"
            sub="From game-day arenas to mission-critical data centers, we deliver floor systems engineered for the loads, chemicals and traffic your operation demands."
          />
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 mt-14">
          {industries.map((ind, i) => {
            const Icon = INDUSTRY_ICONS[ind.icon] || Building;
            return (
              <Reveal key={ind.name} delay={(i % 4) * 80}>
                <div className="group rounded-xl border border-zinc-800 bg-zinc-900 p-6 h-full hover:border-orange-500/50 transition-colors">
                  <span className="w-12 h-12 rounded-lg bg-orange-500/15 flex items-center justify-center group-hover:bg-orange-500 transition-colors">
                    <Icon className="w-6 h-6 text-orange-400 group-hover:text-white transition-colors" />
                  </span>
                  <h3 className="font-display font-bold text-white text-lg mt-4">{ind.name}</h3>
                  <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{ind.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function StatsBar() {
  return (
    <section className="relative -mt-1 bg-orange-500">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-orange-400/40">
          {stats.map((s) => (
            <div key={s.label} className="py-8 px-4 text-center">
              <div className="font-display font-black text-3xl md:text-4xl text-zinc-950">{s.value}</div>
              <div className="text-orange-950/80 text-sm font-medium mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function NotableProjectsSection() {
  return (
    <section className="py-24 bg-zinc-900/50 border-t border-zinc-900">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <div className="max-w-3xl">
            <span className="text-orange-500 font-semibold text-sm tracking-[0.2em] uppercase">Notable Projects</span>
            <h2 className="mt-3 font-display font-extrabold text-white text-3xl md:text-5xl leading-tight tracking-tight">
              Trusted on Landmark Facilities
            </h2>
          </div>
        </Reveal>

        <div className="mt-14 space-y-10">
          {notableProjects.map((p) => (
            <Reveal key={p.id}>
              <div className="grid lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-900">
                <div className="relative h-72 lg:h-auto overflow-hidden group">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/70 to-transparent lg:bg-gradient-to-r" />
                  <span className="absolute top-5 left-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-500 text-white text-xs font-semibold">
                    <Trophy className="w-3.5 h-3.5" /> {p.tag}
                  </span>
                </div>
                <div className="p-8 lg:p-10 flex flex-col justify-center">
                  <h3 className="font-display font-black text-white text-3xl">{p.name}</h3>
                  <div className="mt-2 flex items-center gap-1.5 text-orange-400 text-sm font-medium">
                    <MapPin className="w-4 h-4" /> {p.subtitle}
                  </div>
                  <p className="mt-5 text-zinc-400 leading-relaxed">{p.text}</p>
                  {p.text2 && <p className="mt-4 text-zinc-400 leading-relaxed">{p.text2}</p>}
                  <Link to="/contact" className="mt-7">
                    <Button className="bg-orange-500 hover:bg-orange-600 text-white font-semibold">
                      Start Your Project <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}


function SectionHeading({ eyebrow, title, sub }) {
  return (
    <div className="max-w-3xl">
      <span className="text-orange-500 font-semibold text-sm tracking-[0.2em] uppercase">{eyebrow}</span>
      <h2 className="mt-3 font-display font-extrabold text-white text-3xl md:text-5xl leading-tight tracking-tight">{title}</h2>
      {sub && <p className="mt-4 text-zinc-400 text-lg leading-relaxed">{sub}</p>}
    </div>
  );
}

function ServicesSection() {
  return (
    <section className="py-24 bg-zinc-950">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <SectionHeading
            eyebrow="What We Do"
            title="Floor Systems Engineered for Performance"
            sub="From heavy-duty resinous coatings to polished concrete and moisture mitigation, we tailor every system to your facility's loads, environment and operational demands."
          />
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {featuredServices.map((s, i) => (
            <Reveal key={s.id} delay={i * 80}>
              <div className="group relative rounded-xl overflow-hidden border border-zinc-800 bg-zinc-900 hover-lift h-full">
                <div className="relative h-52 overflow-hidden">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/20 to-transparent" />
                </div>
                <div className="p-6">
                  <h3 className="font-display font-bold text-white text-xl">{s.title}</h3>
                  <p className="mt-3 text-zinc-400 text-sm leading-relaxed">{s.short}</p>
                  <Link to="/services" className="mt-4 inline-flex items-center gap-1.5 text-orange-400 text-sm font-semibold group-hover:gap-2.5 transition-all">
                    Learn more <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function PhilosophySection() {
  return (
    <section className="py-24 bg-zinc-900/50">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="relative">
            <img
              src="/images/polished-topping.jpg"
              alt="Polished concrete installation in progress at a large facility"
              className="rounded-xl w-full h-[460px] object-cover"
            />
            <div className="absolute -bottom-6 -right-6 bg-orange-500 rounded-xl p-6 w-44 shadow-xl hidden md:block">
              <div className="font-display font-black text-4xl text-zinc-950">30+</div>
              <div className="text-orange-950/80 text-sm font-medium">Years of craftsmanship</div>
            </div>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div>
            <SectionHeading eyebrow="Our Philosophy" title="Quality You Can Stand On" />
            <p className="mt-5 text-zinc-400 leading-relaxed">{philosophy.intro}</p>
            <div className="mt-8 space-y-5">
              {[
                { icon: Award, title: "Our Mission", text: philosophy.mission },
                { icon: Sparkles, title: "Our Vision", text: philosophy.vision },
              ].map((item) => (
                <div key={item.title} className="flex gap-4">
                  <span className="flex-shrink-0 w-11 h-11 rounded-lg bg-orange-500/15 flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-orange-400" />
                  </span>
                  <div>
                    <h4 className="font-display font-bold text-white text-lg">{item.title}</h4>
                    <p className="text-zinc-400 text-sm mt-1 leading-relaxed">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ProcessSection() {
  return (
    <section className="py-24 bg-zinc-950">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <SectionHeading eyebrow="Our Process" title="How We Bring Your Floor to Life" />
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {steps.map((step, i) => (
            <Reveal key={step.num} delay={i * 90}>
              <div className="relative rounded-xl border border-zinc-800 bg-zinc-900 p-7 h-full hover:border-orange-500/50 transition-colors">
                <span className="font-display font-black text-5xl text-zinc-800">{step.num}</span>
                <h4 className="font-display font-bold text-white text-lg mt-3">{step.title}</h4>
                <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{step.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="relative py-24 overflow-hidden">
      <div className="absolute inset-0">
        <img src="/images/polished-concrete-new.jpg" alt="Polished concrete facility floor" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-zinc-950/85" />
      </div>
      <div className="relative max-w-4xl mx-auto px-6 text-center">
        <Reveal>
          <ShieldCheck className="w-12 h-12 text-orange-500 mx-auto" />
          <h2 className="mt-5 font-display font-black text-white text-3xl md:text-5xl leading-tight">
            Ready to Coat Your Facility?
          </h2>
          <p className="mt-4 text-zinc-300 text-lg max-w-2xl mx-auto">
            Tell us about your project and timeline. Get a free on-site assessment and a detailed proposal tailored to your facility.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link to="/contact">
              <Button className="h-12 px-8 bg-orange-500 hover:bg-orange-600 text-white font-semibold text-base">
                Request a Consultation <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <a href={`tel:${company.phone}`}>
              <Button variant="outline" className="h-12 px-8 border-zinc-600 bg-white/5 text-white hover:bg-white/10 font-semibold text-base">
                <Phone className="w-4 h-4 mr-1" /> {company.phone}
              </Button>
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <StatsBar />
      <ServicesSection />
      <IndustriesSection />
      <NotableProjectsSection />
      <PhilosophySection />
      <ProcessSection />
      <CTASection />
    </>
  );
}
